import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Play, Save, Footprints, Target, Flame, Clock, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { SyncIndicator } from './SyncIndicator';


// Storage key for step goal settings
const STEP_GOAL_KEY = 'walking_step_goal_settings';
const DAILY_STEPS_KEY = 'walking_daily_steps';
const STRIDE_LENGTH_KEY = 'walking_stride_length';

// Get today's date string for daily tracking
const getTodayKey = () => new Date().toISOString().split('T')[0];

export default function WalkingSetup({ onStartWorkout, onBack, isExpired, onNavigate }) {
  const [config, setConfig] = useState({
    quickWalkingTime: 3,      // 3-15 minutes
    slowWalkingTime: 3,       // 3-10 minutes
    quickWalkingSets: 4,      // 4-10 sets
    slowWalkingSets: 4,       // 4-15 sets
  });
  const [routineName, setRoutineName] = useState('');
  
  // Load body weight from shared localStorage (set in Body Info)
  const bodyWeight = (() => {
    const gymWeight = localStorage.getItem('gym_notebook_persistent_bodyweight');
    return gymWeight ? parseFloat(gymWeight) : 70;
  })();
  
  // Load stride length from shared localStorage (set in Body Info)
  const strideLength = (() => {
    const saved = localStorage.getItem(STRIDE_LENGTH_KEY);
    return saved ? parseInt(saved) : 75;
  })();
  
  // Step goal state
  const [stepGoalEnabled, setStepGoalEnabled] = useState(() => {
    const saved = localStorage.getItem(STEP_GOAL_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.enabled || false;
    }
    return false;
  });
  
  const [stepGoal, setStepGoal] = useState(() => {
    const saved = localStorage.getItem(STEP_GOAL_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.goal || 10000;
    }
    return 10000;
  });
  
  const [todaySteps, setTodaySteps] = useState(() => {
    const dailySteps = localStorage.getItem(DAILY_STEPS_KEY);
    if (dailySteps) {
      const stepsData = JSON.parse(dailySteps);
      const todayKey = getTodayKey();
      return stepsData[todayKey] || 0;
    }
    return 0;
  });

  // Save step goal settings when changed
  useEffect(() => {
    localStorage.setItem(STEP_GOAL_KEY, JSON.stringify({
      enabled: stepGoalEnabled,
      goal: stepGoal
    }));
  }, [stepGoalEnabled, stepGoal]);

  const handleStart = () => {
    onStartWorkout({
      ...config,
      quickWalkingTime: config.quickWalkingTime * 60,  // Convert to seconds
      slowWalkingTime: config.slowWalkingTime * 60,    // Convert to seconds
      stepGoalEnabled,
      stepGoal,
      todaySteps,
      bodyWeight,  // For calorie calculation
      strideLength // For GPS step calculation
    });
  };

  const handleSaveRoutine = () => {
    if (!routineName.trim()) {
      toast.error('Please enter a routine name');
      return;
    }
    
    const saved = localStorage.getItem('gym_notebook_walking_routines');
    const savedRoutines = saved ? JSON.parse(saved) : [];
    
    const newRoutine = {
      id: Date.now(),
      name: routineName,
      quickWalkingTime: config.quickWalkingTime * 60,
      slowWalkingTime: config.slowWalkingTime * 60,
      quickWalkingSets: config.quickWalkingSets,
      slowWalkingSets: config.slowWalkingSets
    };
    
    const updated = [...savedRoutines, newRoutine];
    localStorage.setItem('gym_notebook_walking_routines', JSON.stringify(updated));
    toast.success('Walking routine saved!');
    setRoutineName('');
  };

  // Generate options for quick walking time (3-15 minutes)
  const quickTimeOptions = [];
  for (let i = 3; i <= 15; i++) {
    quickTimeOptions.push(i);
  }

  // Generate options for slow walking time (3-10 minutes)
  const slowTimeOptions = [];
  for (let i = 3; i <= 10; i++) {
    slowTimeOptions.push(i);
  }

  // Generate options for quick walking sets (4-10)
  const quickSetsOptions = [];
  for (let i = 4; i <= 10; i++) {
    quickSetsOptions.push(i);
  }

  // Generate options for slow walking sets (4-15)
  const slowSetsOptions = [];
  for (let i = 4; i <= 15; i++) {
    slowSetsOptions.push(i);
  }

  // Step goal preset options
  const stepGoalOptions = [5000, 7500, 10000, 12500, 15000, 20000];

  // Calculate total workout time
  const totalQuickTime = config.quickWalkingTime * config.quickWalkingSets;
  const totalSlowTime = config.slowWalkingTime * config.slowWalkingSets;
  const totalTime = totalQuickTime + totalSlowTime;
  const hours = Math.floor(totalTime / 60);
  const minutes = totalTime % 60;

  // Calculate step goal progress
  const stepsRemaining = Math.max(0, stepGoal - todaySteps);
  const stepsExceeded = todaySteps > stepGoal ? todaySteps - stepGoal : 0;
  const progressPercent = Math.min(100, (todaySteps / stepGoal) * 100);

  return (
    <>
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950 border-none shadow-none" style={{ paddingTop: 'max(16px, env(safe-area-inset-top, 16px))' }}>
        <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-3 flex items-center justify-between max-w-full overflow-hidden">
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/20 flex-shrink-0">
              <Footprints className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-base sm:text-xl font-heading font-bold tracking-tight text-white truncate">Walking</h1>
            <SyncIndicator compact={true} showLabel={false} />
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-4 pb-24 max-w-lg space-y-6">

      {/* Step Goal Card - Optional */}
      <Card className={`bg-slate-900/80 border-slate-800 rounded-2xl ${stepGoalEnabled ? 'border-violet-500/30' : ''}`}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                <Target className="h-5 w-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-white">Daily Step Goal</CardTitle>
                <CardDescription className="text-slate-400">Optional - Track your daily step progress</CardDescription>
              </div>
            </div>
            <button
              onClick={() => setStepGoalEnabled(!stepGoalEnabled)}
              className={`w-12 h-6 rounded-full transition-colors relative ${
                stepGoalEnabled ? 'bg-violet-500' : 'bg-slate-600'
              }`}
              data-testid="step-goal-toggle"
            >
              <div className={`absolute top-1 w-4 h-4 rounded-full transition-all ${
                stepGoalEnabled 
                  ? 'right-1 bg-white' 
                  : 'left-1 bg-slate-400'
              }`} />
            </button>
          </div>
        </CardHeader>
        
        {stepGoalEnabled && (
          <CardContent className="space-y-4">
            {/* Goal Setting */}
            <div className="space-y-2">
              <Label className="text-white">Step Goal</Label>
              <Select
                value={stepGoal.toString()}
                onValueChange={(v) => setStepGoal(parseInt(v))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="step-goal-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {stepGoalOptions.map((goal) => (
                    <SelectItem key={goal} value={goal.toString()}>
                      {goal.toLocaleString()} steps
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Custom Goal Input */}
            <div className="flex items-center gap-2">
              <Input
                type="number"
                placeholder="Custom goal..."
                value={stepGoal}
                onChange={(e) => setStepGoal(Math.max(1000, parseInt(e.target.value) || 1000))}
                className="flex-1 bg-slate-800 border-slate-700 text-white"
                min="1000"
                max="50000"
                step="500"
                data-testid="step-goal-input"
              />
              <span className="text-sm text-slate-400">steps</span>
            </div>
            
            {/* Today's Progress */}
            <div className="p-4 bg-violet-500/10 rounded-xl border border-violet-500/20 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-violet-300">Today's Progress</span>
                <span className="font-bold text-violet-400">
                  {todaySteps.toLocaleString()} / {stepGoal.toLocaleString()}
                </span>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-slate-700 rounded-full h-3 overflow-hidden">
                <div 
                  className={`h-full transition-all duration-500 ${
                    progressPercent >= 100 ? 'bg-green-500' : 'bg-violet-500'
                  }`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              
              {/* Steps Remaining or Exceeded */}
              <div className="text-center">
                {stepsExceeded > 0 ? (
                  <span className="text-green-400 font-bold text-lg">
                    🎉 +{stepsExceeded.toLocaleString()} steps over goal!
                  </span>
                ) : (
                  <span className="text-violet-300">
                    <span className="font-bold text-2xl text-violet-400">{stepsRemaining.toLocaleString()}</span>
                    <span className="text-sm ml-1">steps to go</span>
                  </span>
                )}
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Calorie Estimate Card */}
      <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center">
              <Flame className="h-5 w-5 text-white" />
            </div>
            <CardTitle className="text-white">Calorie Estimate</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="p-4 bg-orange-500/10 rounded-xl border border-orange-500/20">
            <div className="flex items-center justify-between text-sm">
              <span className="text-orange-300">Est. calories for this workout:</span>
              <span className="font-bold text-orange-400 text-xl">
                ~{Math.round(
                  // Quick walking: MET 5.0, Slow walking: MET 2.5
                  (5.0 * bodyWeight * (config.quickWalkingTime * config.quickWalkingSets / 60)) +
                  (2.5 * bodyWeight * (config.slowWalkingTime * config.slowWalkingSets / 60))
                )} kcal
              </span>
            </div>
            <p className="text-[10px] text-slate-500 mt-2">
              Based on {bodyWeight}kg body weight (set in Personal → Body Info)
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Quick Walking Card */}
      <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <Zap className="h-5 w-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-white">Quick Walking</CardTitle>
              <CardDescription className="text-slate-400">High-intensity fast walking intervals</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="quickTime" className="text-white">Duration (minutes)</Label>
              <Select
                value={config.quickWalkingTime.toString()}
                onValueChange={(v) => setConfig({ ...config, quickWalkingTime: parseInt(v) })}
              >
                <SelectTrigger id="quickTime" className="bg-slate-800 border-slate-700 text-white" data-testid="quick-walking-time-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {quickTimeOptions.map((time) => (
                    <SelectItem key={time} value={time.toString()}>
                      {time} minutes
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="quickSets" className="text-white">Number of Sets</Label>
              <Select
                value={config.quickWalkingSets.toString()}
                onValueChange={(v) => setConfig({ ...config, quickWalkingSets: parseInt(v) })}
              >
                <SelectTrigger id="quickSets" className="bg-slate-800 border-slate-700 text-white" data-testid="quick-walking-sets-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {quickSetsOptions.map((sets) => (
                    <SelectItem key={sets} value={sets.toString()}>
                      {sets} sets
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="p-3 bg-cyan-500/10 rounded-xl border border-cyan-500/20 text-sm">
            <span className="text-cyan-300">Total quick walking: </span>
            <span className="font-bold text-white">{totalQuickTime} minutes</span>
          </div>
        </CardContent>
      </Card>

      {/* Slow Walking Card */}
      <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
              <Footprints className="h-5 w-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-white">Slow Walking</CardTitle>
              <CardDescription className="text-slate-400">Recovery walking intervals</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="slowTime" className="text-white">Duration (minutes)</Label>
              <Select
                value={config.slowWalkingTime.toString()}
                onValueChange={(v) => setConfig({ ...config, slowWalkingTime: parseInt(v) })}
              >
                <SelectTrigger id="slowTime" className="bg-slate-800 border-slate-700 text-white" data-testid="slow-walking-time-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {slowTimeOptions.map((time) => (
                    <SelectItem key={time} value={time.toString()}>
                      {time} minutes
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="slowSets" className="text-white">Number of Sets</Label>
              <Select
                value={config.slowWalkingSets.toString()}
                onValueChange={(v) => setConfig({ ...config, slowWalkingSets: parseInt(v) })}
              >
                <SelectTrigger id="slowSets" className="bg-slate-800 border-slate-700 text-white" data-testid="slow-walking-sets-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {slowSetsOptions.map((sets) => (
                    <SelectItem key={sets} value={sets.toString()}>
                      {sets} sets
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="p-3 bg-green-500/10 rounded-xl border border-green-500/20 text-sm">
            <span className="text-green-300">Total slow walking: </span>
            <span className="font-bold text-white">{totalSlowTime} minutes</span>
          </div>
        </CardContent>
      </Card>

      {/* Total Summary */}
      <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
        <CardContent className="p-6">
          <div className="text-center space-y-2">
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
                <Clock className="h-4 w-4 text-white" />
              </div>
              <span className="text-sm text-slate-400">Estimated Total Workout Time</span>
            </div>
            <div className="text-4xl font-heading font-bold text-blue-400">
              {hours > 0 ? `${hours}h ${minutes}m` : `${minutes} minutes`}
            </div>
            <div className="text-xs text-slate-500">
              {config.quickWalkingSets + config.slowWalkingSets} total intervals
            </div>
          </div>
        </CardContent>
      </Card>

      <Button
        onClick={handleStart}
        className="w-full h-14 text-lg font-semibold bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 rounded-2xl"
        size="lg"
        data-testid="start-walking-workout-btn"
      >
        <Play className="mr-2 h-6 w-6" />
        Start Walking Workout
      </Button>

      {/* Save Routine */}
      <div className="flex gap-2">
        <Input
          placeholder="Routine name..."
          value={routineName}
          onChange={(e) => setRoutineName(e.target.value)}
          className="bg-slate-800 border-slate-700 text-white"
        />
        <Button onClick={handleSaveRoutine} variant="secondary" className="bg-slate-800 hover:bg-slate-700 text-white">
          <Save className="mr-2 h-4 w-4" />
          Save
        </Button>
      </div>
      </div>
    </div>
    </>
  );
}
